<template>
  <div
    v-show="gridVisible"
    class="grid-wrapper gridScroll"
    style="height: 100%"
  >
    <ag-grid-vue
      style="height: 100%"
      class="ag-theme-balham"
      :grid-options="gridOptions"
      :column-defs="getColumns"
      :row-data="rows"
      :row-selection="rowSelection"
      :default-col-def="defaultColDef"
      :overlay-loading-template="overlayLoadingTemplate"
      :framework-components="frameworkComponents"
      :context="context"
      :suppress-row-transform="suppressRowTransform"
      :get-row-height="getRowHeight"
      :stop-editing-when-cells-lose-focus="true"
      :enable-browser-tooltips="true"
      :tooltip-hide-delay="tooltipHideDelay"
      :tooltip-show-delay="tooltipShowDelay"
      :suppress-row-click-selection="suppressRowClickSelection"
      :pinned-top-row-data="pinnedTopRowData"
      :pinned-bottom-row-data="pinnedBottomRowData"
      :row-class-rules="rowClassRules"
      :modules="modules"
      :group-use-entire-row="groupUseEntireRow"
      :auto-group-column-def="autoGroupColumnDef"
      :tree-data="treeData"
      :get-data-path="getDataPath"
      :group-default-expanded="groupDefaultExpanded"
      :group-selects-children="groupSelectsChildren"
      :is-row-selectable="isRowSelectable"
      :row-multi-select-with-click="rowMultiSelectWithClick"
      :enable-range-selection="enableRangeSelection"
      :allow-context-menu-with-control-key="allowContextMenuWithControlKey"
      :get-context-menu-items="getContextMenuItems"
      :is-full-width-cell="isFullWidthRow"
      :full-width-cell-renderer="fullWidthCellRenderer"
      :embed-full-width-rows="embedFullWidthRows"
      :group-include-footer="groupIncludeFooter"
      :group-suppress-blank-header="true"
      @sortChanged="onSortChanged"
      @columnResized="columnResized"
      @selection-changed="onSelectionChanged"
      @rowClicked="onRowClicked"
      @cellClicked="onCellClicked"
      @first-data-rendered="onFirstDataRendered"
      @cellValueChanged="rowValueChanged"
    />
  </div>
</template>
<script>
import { AgGridVue } from '@ag-grid-community/vue'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping'
import { MenuModule } from '@ag-grid-enterprise/menu'
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel'
import { SetFilterModule } from '@ag-grid-enterprise/set-filter'
import FullWidthCellRenderer from './fullWidthCellRenderer.js'
import { DEFAULT_GRID_ROW_HEIGHT, DEFAULT_GRID_HEADER_HEIGHT } from '@/constant'

export default {
  components: {
    AgGridVue
    // eslint-disable-next-line vue/no-unused-components
    // FullWidthCellRenderer
  },
  props: {
    columns: {
      type: Array,
      default: () => []
    },
    rows: {
      type: Array,
      default: () => []
    },
    disabledSortable: {
      type: Boolean,
      default: true
    },
    // frameworkComponents: {
    //   type: Object,
    //   default: () => null
    // },
    context: {
      type: Object,
      default: () => {}
    },
    suppressRowTransform: {
      type: Boolean,
      default: false
    },
    rowSelection: {
      type: String,
      default: 'multiple'
    },
    pagination: {
      type: Boolean,
      default: false
    },
    defaultColDef: {
      type: Object,
      default: () => {
        return {
          suppressMovable: true,
          resizable: false,
          sortable: true
        }
      }
    },
    groupUseEntireRow: {
      type: Boolean,
      default: true
    },
    treeData: {
      type: Boolean,
      default: false
    },

    autoGroupColumnDef: {
      type: Object,
      default: () => {
        return {
          maxWidth: 200,
          headerName: '',
          cellRendererParams: {
            suppressCount: true
            // checkbox: true,
          }
        }
      }
    },
    groupSelectsChildren: {
      type: Boolean,
      default: false
    },
    groupDefaultExpanded: {
      type: Number,
      default: -1
    },
    groupField: {
      type: String,
      default: ''
    },
    pinnedTopRowData: {
      type: Array,
      default: null
    },
    pinnedBottomRowData: {
      type: Array,
      default: null
    },
    headerHeight: {
      type: Number,
      default: DEFAULT_GRID_HEADER_HEIGHT
    },
    breakPoint: {
      type: Number,
      default: 1200
    },
    domLayout: {
      type: String,
      default: 'autoHeight'
    },
    suppressRowClickSelection: {
      type: Boolean,
      default: false
    },
    getDataPath: {
      default: null
    },
    rowHeight: {
      default: 40
    },
    rowClassRules: null,
    reRenderRow: {
      default: false
    },
    autoSizeCell: {
      type: Boolean,
      default: false
    },
    isRowSelectable: {
      type: Function,
      default: () => true
    },
    enableRangeSelection: {
      type: Boolean,
      default: false
    },
    allowContextMenuWithControlKey: {
      type: Boolean,
      default: () => false
    },
    getContextMenuItems: {
      type: Function,
      default: () => {}
    },
    suppressContextMenu: {
      type: Boolean,
      default: true
    },
    // fullWidthCellRenderer: null,
    embedFullWidthRows: {
      type: Boolean,
      default: false
    },
    groupIncludeFooter: {
      type: Boolean,
      default: () => false
    },
    rowMultiSelectWithClick: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      gridVisible: false,
      sortColumn: null,
      sortOrder: null, // 1:Ascending, 2:Descending
      gridOptions: null,
      gridApi: null,
      overlayLoadingTemplate:
        '<span class="ag-overlay-loading-center"><div class="loader">Loading...</div></span>',
      selectedData: null,
      // getDataPath: null,
      // isRowSelectable: null,
      scrollUse: false,
      // rowHeight: 38,

      tooltipShowDelay: 0,
      tooltipHideDelay: 0,
      modules: [
        ClientSideRowModelModule,
        RowGroupingModule,
        MenuModule,
        ColumnsToolPanelModule,
        SetFilterModule
      ],
      isFullWidthRow: null,
      fullWidthCellRenderer: null,
      frameworkComponents: null
    }
  },
  computed: {
    getColumns() {
      const disabled = this.disabledSortable
      if (disabled === null) {
        return this.columns
      } else {
        return this.columns.map(c => ({
          ...c,
          comparator: () => {
            return 0
          }
        }))
      }
    },
    getSideState() {
      return this.$store.getters['ui/getSideState']
    },
    getTab() {
      return this.$store.getters['tab']
    }
  },
  watch: {
    rows() {
      setTimeout(() => {
        this.deviceScrollCheck()
        this.gridOptions.api.forEachNode(function (node) {
          if (node.data?.inProduct) {
            node.setSelected(node.data.inProduct)
          }
        })
      })
    },
    selectedData() {
      this.$emit('changeSelectedData', this.selectedData)
    },
    reRenderRow() {
      if (this.reRenderRow) {
        this.refreshAll()
      }
    },
    getSideState() {
      setTimeout(() => {
        this.sizeToFit()
      }, 500)
    },
    getTab() {
      this.gridApi.sizeColumnsToFit()
    }
  },
  created() {
    // if (this.treeData) {
    // this.getDataPath = data => {
    //   return data[this.groupField]
    // }
    // this.getDataPath = data => {
    //   return data.orgHierarchy
    // }
    // }
    // this.sizeToFit();
    // setTimeout(this.sizeToFit);
    window.addEventListener('resize', this.deviceScrollCheck)
    setTimeout(() => {
      this.gridVisible = true
    })
  },
  beforeMount() {
    this.gridOptions = {
      pagination: false,
      paginationAutoPageSize: false,
      domLayout: this.domLayout,
      headerHeight: this.headerHeight,
      // rowHeight: this.rowHeight,
      localeText: { noRowsToShow: 'Không có dữ liệu.' },
      suppressContextMenu: this.suppressContextMenu,
      onRowDoubleClicked: this.onDoubleClick,
      groupDefaultExpanded: this.groupDefaultExpanded
      // isFullWidthRow: params => {
      //   if (params.rowNode.data.isFullWidthRow) {
      //     return true
      //   } else {
      //     return false
      //   }
      // },
      // fullWidthCellRenderer: FullWidthCellRenderer,
      // getRowId: params => params.data.id,
      // wrapHeaderText: true,
    }
    this.isFullWidthRow = params => {
      if (params?.data?.isFullWidthRow) {
        return true
      } else {
        return false
      }
    }

    this.frameworkComponents = { fullWidthCellRenderer: FullWidthCellRenderer }
    this.fullWidthCellRenderer = 'fullWidthCellRenderer'
    // if (!this.fullWidthCellRenderer) {
    //   this.fullWidthCellRendererComp = 'FullWidthCellRenderer'
    // } else {
    //   this.fullWidthCellRendererComp = this.fullWidthCellRenderer
    // }
  },
  mounted() {
    this.gridApi = this.gridOptions.api
    const gridApis = [this.gridApi]
    this.$store.commit('ui/setGridApi', gridApis)
    // setTimeout(() => {
    //   this.deviceScrollCheck(); //데이터가 없는 경우 대비
    //   document
    //     .querySelector('.ag-body-horizontal-scroll-viewport')
    //     .addEventListener('scroll', () => {
    //       setTimeout(() => {
    //         this.deviceScrollCheck();
    //       }, 100);
    //     });
    // });
    // this.sizeToFit()
    // setInterval(() => {
    //   this.gridApi.sizeColumnsToFit()
    // }, 500)
    this.gridApi.sizeColumnsToFit()
  },
  beforeDestroy() {
    // window.removeEventListener('resize', this.deviceScrollCheck);
  },
  methods: {
    onRowClick(event) {
      if (event.targetType === 'cell') {
        const { rowKey } = event
        rowKey
        this.$emit('rowClick', this.rows[rowKey])
      }
    },
    onSortChanged(e) {
      this.sortColumn = null
      this.sortOrder = null
      e.columnApi.getColumnState().forEach(c => {
        if (c.sort !== null) {
          this.sortColumn = c.colId
          // this.sortOrder = c.sort === 'asc' ? 1 : 2;
          this.sortOrder = c.sort
          return false
        }
      })
      this.doRefresh()
    },
    doRefresh() {
      const params = {
        orderBy: this.sortColumn,
        sort: this.sortOrder
      }
      this.$emit('refresh', params)
    },
    camelToSnakeCase(field) {
      return field.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
    },
    onSelectionChanged() {
      const selectedRows = this.gridApi.getSelectedRows()
      this.$emit('selectionChanged', selectedRows)
    },
    onRowClicked() {
      const selectedRows = this.gridApi.getSelectedRows()
      this.$emit('rowClicked', selectedRows)
    },
    columnResized(event) {
      this.$emit('columnResized', event)
    },
    onCellClicked(params) {},
    getRowSelected() {
      const selectedNodes = this.gridApi.getSelectedNodes()
      this.selectedData = selectedNodes.map(node => node.data)
      return this.selectedData
    },
    onDoubleClick() {
      const selectedRows = this.gridApi.getSelectedRows()
      this.$emit('doubleClicked', selectedRows)
    },
    // eslint-disable-next-line no-unused-vars
    onFirstDataRendered(params) {
      // params.api.sizeColumnsToFit();
      this.deviceScrollCheck()
    },
    // sizeToFit() {
    //   // this.gridApi.sizeColumnsToFit();
    //   if (window.innerWidth > this.breakPoint) {
    //     console.log(12345)
    //     this.gridApi.sizeColumnsToFit()
    //   }
    // },
    autoSizeAll() {
      // console.log('autoSizeAll() 실행')
      // // console.log('haha: ', this.gridOptions.columnApi)
      // const allColumnIds = []
      // if (this.gridOptions?.columnApi) {
      //   this.gridOptions.columnApi.getColumns().forEach(column => {
      //     allColumnIds.push(column.getId())
      //   })
      //   this.gridOptions.columnApi.autoSizeColumns(allColumnIds, false)
      // }
    },
    deviceScrollCheck() {
      this.gridApi.sizeColumnsToFit()
      // this.sizeToFit()
      // this.autoSizeAll()
      // console.log('deviceScrollCheck')
      // this.sizeToFit()
      // if (this.autoSizeCell) {
      //   this.scrollUse = true
      //   return
      // }
      // if (window.innerWidth <= this.breakPoint) {
      //   this.autoSizeAll()
      //   this.scrollUse = true
      //   console.log('auto size all')
      // } else {
      //   this.scrollUse = false
      // }
    },
    // getRowHeight(params) {
    //   // consul && fault receipt
    //   let length = 1
    //   if (
    //     (params.data && params.data.apl_tp) ||
    //     (params.data && params.data.app_tps) ||
    //     (params.data && params.data.cust_coms)
    //   ) {
    //     for (const property in params.data) {
    //       const stringItem = String(params.data[property])
    //       if (params.data[property] && stringItem.includes('\n')) {
    //         if (length < stringItem.split('\n').length) {
    //           length = stringItem.split('\n').length
    //         }
    //       }
    //     }
    //     const height = 34 * length
    //     return height
    //   }
    //   return this.rowHeight
    // },
    getRowHeight(params) {
      // console.log('getRowHeight: ', params.data)
      let length = 1
      for (const property in params.data) {
        const stringItem = String(params.data[property])
        if (params.data[property] && stringItem.includes('<br/>')) {
          if (length < stringItem.split('<br/>').length) {
            length = stringItem.split('<br/>').length
          }
        }
      }
      const height = DEFAULT_GRID_ROW_HEIGHT * length
      return height
    },
    setGroupHeight(height) {
      // eslint-disable-next-line
      this.rowHeight = height
      this.gridApi.resetRowHeights()
    },
    rowValueChanged() {
      const selectedRows = this.gridApi.getSelectedRows()
      this.$emit('rowValueChanged', selectedRows)
    },
    refreshAll() {
      // alert(3);
      // this.gridApi.refreshCells();
      this.gridApi.redrawRows()
      // this.gridApi.refreshRows();
    }
  }
}
</script>

<!-- <style>
.rag-green-outer {
  background-color: #f2abb4 !important;
}
</style> -->
